// src/features/dashboard/teacher/EditStudentScheduleModal.jsx
import React, { useState, useEffect } from 'react';
import Modal from '../../../components/ui/Modal';

const EditStudentScheduleModal = ({ isOpen, onClose, student, onSave }) => {
  const [schedule, setSchedule] = useState([]);
  const [termData, setTermData] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && student) {
      setSchedule(student.schedule || [{ day: '', startTime: '', duration: '' }]);
      // ✅ Capture termData from student
      setTermData(student.termData || [{ term: '', termStart: '', termEnd: '' }]);
    }
  }, [isOpen, student]);

  // ---- Term handlers ----
  const addTermRow = () => {
    setTermData([...termData, { term: '', termStart: '', termEnd: '' }]);
  };

  const removeTermRow = (index) => {
    if (termData.length <= 1) return;
    const newTermData = termData.filter((_, i) => i !== index);
    setTermData(newTermData);
  };

  const updateTerm = (index, field, value) => {
    const newTermData = [...termData];
    newTermData[index][field] = value;
    setTermData(newTermData);
  };

  // ---- Schedule handlers ----
  const addSession = () => {
    setSchedule([...schedule, { day: '', startTime: '', duration: '' }]);
  };

  const removeSession = (index) => {
    if (schedule.length <= 1) return;
    const newSchedule = schedule.filter((_, i) => i !== index);
    setSchedule(newSchedule);
  };

  const updateSession = (index, field, value) => {
    const newSchedule = [...schedule];
    newSchedule[index][field] = value;
    setSchedule(newSchedule);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validSessions = schedule.filter(s => s.day && s.startTime && s.duration);
    if (validSessions.length === 0) {
      alert('Please add at least one valid session.');
      return;
    }

    // ✅ Filter out empty term rows
    const validTerms = termData.filter(t => t.term && t.termStart && t.termEnd);

    setSaving(true);
    try {
      // ✅ Pass both schedule and termData
      //await onSave(student.userId, schedule, validTerms);
      await onSave(student.rowNumber, schedule, validTerms);
      onClose();
    } catch (err) {
      alert('Failed to update schedule: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <h3 className="text-xl font-bold mb-4">
        Edit Schedule – {student?.name} {student?.surname}
      </h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* ===== TERM DATA SECTION ===== */}
        <div className="border-t pt-3">
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-medium">Term Dates</h4>
            <button
              type="button"
              onClick={addTermRow}
              className="text-sm bg-purple-600 text-white px-2 py-1 rounded hover:bg-purple-700"
            >
              + Add Term
            </button>
          </div>
          {termData.map((term, idx) => (
            <div key={idx} className="grid grid-cols-3 gap-2 mb-2 items-end">
              <div>
                <label className="block text-xs font-medium">Term Name</label>
                <input
                  type="text"
                  value={term.term || ''}
                  onChange={e => updateTerm(idx, 'term', e.target.value)}
                  placeholder="e.g., Term 1"
                  className="w-full border rounded px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium">Start Date</label>
                <input
                  type="date"
                  value={term.termStart || ''}
                  onChange={e => updateTerm(idx, 'termStart', e.target.value)}
                  className="w-full border rounded px-2 py-1 text-sm"
                />
              </div>
              <div className="flex items-end gap-1">
                <div className="flex-1">
                  <label className="block text-xs font-medium">End Date</label>
                  <input
                    type="date"
                    value={term.termEnd || ''}
                    onChange={e => updateTerm(idx, 'termEnd', e.target.value)}
                    className="w-full border rounded px-2 py-1 text-sm"
                  />
                </div>
                {termData.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeTermRow(idx)}
                    className="text-red-600 hover:text-red-800 text-sm px-1 py-1"
                    title="Remove term"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          ))}
          {termData.length === 0 && (
            <p className="text-sm text-gray-500">No terms added. Click "+ Add Term" to add one.</p>
          )}
        </div>

        {/* ===== SCHEDULE SECTION ===== */}
        <div className="border-t pt-3">
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-medium">Sessions</h4>
            <button
              type="button"
              onClick={addSession}
              className="text-sm bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
            >
              + Add Session
            </button>
          </div>
          {schedule.map((session, idx) => (
            <div key={idx} className="grid grid-cols-3 gap-2 mb-2 items-end">
              <div>
                <label className="block text-xs font-medium">Day</label>
                <input
                  type="text"
                  value={session.day || ''}
                  onChange={e => updateSession(idx, 'day', e.target.value)}
                  placeholder="e.g., Monday"
                  className="w-full border rounded px-2 py-1 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium">Start Time</label>
                <input
                  type="text"
                  value={session.startTime || ''}
                  onChange={e => updateSession(idx, 'startTime', e.target.value)}
                  placeholder="e.g., 09:00"
                  className="w-full border rounded px-2 py-1 text-sm"
                  required
                />
              </div>
              <div className="flex items-end gap-1">
                <div className="flex-1">
                  <label className="block text-xs font-medium">Duration (min)</label>
                  <input
                    type="number"
                    value={session.duration || ''}
                    onChange={e => updateSession(idx, 'duration', e.target.value)}
                    placeholder="60"
                    className="w-full border rounded px-2 py-1 text-sm"
                    required
                  />
                </div>
                {schedule.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeSession(idx)}
                    className="text-red-600 hover:text-red-800 text-sm px-1 py-1"
                    title="Remove session"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          ))}
          {schedule.length === 0 && (
            <p className="text-sm text-gray-500">No sessions added. Click "+ Add Session" to add one.</p>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Schedule'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EditStudentScheduleModal;